import {
  ADD_CLIENT_SUCCESS,
  ADD_COMPANY_SUCCESS,
  Company,
  UPDATE_CLIENTS_SUCCESS,
} from 'src/store/clients/types';
import { appAPI, ApiTags, ApiMethods } from '.';
import { Client } from 'src/constants';
import { RootState } from 'src/store';
import { MODIFY_INTERNAL_USERS } from 'src/store/users/types';

type getCompaniesInput = {
  ignoreAccess?: boolean;
};

type ClientInput = { firstName: string; lastName: string; email: string };
type CompanyInput = {
  name: string;
  clients: Array<ClientInput>;
};

export type UnprocessedClientResponse = {
  client: ClientInput;
  error: string;
};

type CreateCompanyResponse = {
  company: Company;
  createdClients: Client[];
  updatedClients: Client[];
  unprocessedClients: Array<UnprocessedClientResponse>;
};

export const companiesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    createCompany: build.mutation<CreateCompanyResponse, CompanyInput>({
      query: (newCompany) => ({
        path: `/v1/companies`,
        method: ApiMethods.post,
        options: {
          body: newCompany,
        },
      }),
      invalidatesTags: [
        ApiTags.companies,
        ApiTags.clientCompanies,
        ApiTags.clientUsers,
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        try {
          const state = getState() as RootState;
          const currentUserId = state.user.id;
          const { internalUsers } = state.users;
          const { data } = await queryFulfilled;
          if (data.createdClients.length) {
            dispatch({
              type: ADD_CLIENT_SUCCESS,
              payload: data.createdClients,
            });
          }
          if (data.updatedClients.length) {
            dispatch({
              type: UPDATE_CLIENTS_SUCCESS,
              payload: data.updatedClients,
            });
          }
          if (data.company) {
            dispatch({ type: ADD_COMPANY_SUCCESS, payload: [data.company] });
            dispatch({
              type: MODIFY_INTERNAL_USERS,
              payload: internalUsers.map((user) => {
                if (user.id === currentUserId) {
                  return {
                    ...user,
                    companyAccessList: [
                      ...(user.companyAccessList ?? []),
                      data.company,
                    ],
                  };
                }
                return user;
              }),
            });
          }
        } catch (e) {
          // nothing for now
        }
      },
    }),
    getCompanies: build.query<Company[], getCompaniesInput>({
      query: (getCompaniesInput) => ({
        path: `/v1/companies`,
        method: ApiMethods.get,
        options: {
          queryStringParameters: getCompaniesInput,
        },
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
    manageCompanyClients: build.mutation<
      {
        addedClientIds: string[];
        addClientErrors: string[];
        removedClientIds: string[];
        removeClientErrors: string[];
      },
      { companyId: string; addClientIds: string[]; removeClientIds: string[] }
    >({
      query: ({ companyId, addClientIds, removeClientIds }) => ({
        path: `/v1/companies/${companyId}/clients`,
        method: ApiMethods.post,
        options: { body: { addClientIds, removeClientIds } },
      }),
    }),
    getClientCompanies: build.query<Company[], void>({
      query: () => ({
        path: `/v1/companies/client-companies`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.clientCompanies],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useCreateCompanyMutation,
  useManageCompanyClientsMutation,
  useGetClientCompaniesQuery,
} = companiesApi;
